// ── company-archive-modal: the archive confirm Modal (PATCH status=archived) ─
// Wave 1 (Companies). Archiving is a PATCH that sets status:"archived" — there
// is NO destructive delete. This modal is a deliberate confirmation step so an
// archive is never a one-click accident; the page owns the actual PATCH and
// toasts only after the backend confirms. A refused write (permission-denied /
// gate-closed / unavailable) reads as its honest message inline and the dialog
// stays open — a closed write NEVER implies the company was archived.

import { Alert, Button, Modal } from "@vjal/ui";

import { useHonestQuery } from "../../components/honest-state";
import { destructiveButtonClass } from "../../components/ui-classes";
import { coachingQueryKeys } from "../../hooks/coaching-query-keys";
import { getCoachingServices } from "../../services/coaching-service-provider";
import type { CoachingCohortDto, CoachingCompanyDto, CoachingProgramDto } from "../../services/coaching-dtos";
import { describeCompanyWriteError } from "./companies-write";

const services = getCoachingServices();
export interface CompanyArchiveModalProps {
  open: boolean;
  company: CoachingCompanyDto | null;
  onClose: () => void;
  /** The actual archive PATCH — owned by the page (react-query mutation). */
  onConfirm: () => Promise<unknown>;
  submitting?: boolean;
  submitError?: unknown;
}

export function CompanyArchiveModal({
  open,
  company,
  onClose,
  onConfirm,
  submitting = false,
  submitError
}: CompanyArchiveModalProps) {
  const topError = describeCompanyWriteError(submitError);
  const name = company?.name || "this company";
  const companyId = company?.id ?? "";
  const programsQuery = useHonestQuery(
    coachingQueryKeys.list("programs.list", { company_id: companyId }),
    () => services.programs.list({ company_id: companyId }),
    { enabled: open && company !== null, isEmpty: () => false }
  );
  const cohortsQuery = useHonestQuery(
    coachingQueryKeys.list("cohorts.list", { company_id: companyId }),
    () => services.cohorts.list({ company_id: companyId }),
    { enabled: open && company !== null, isEmpty: () => false }
  );
  const countsFailed = programsQuery.error != null || cohortsQuery.error != null;
  const countsLoading = !countsFailed && (programsQuery.posture === "loading" || cohortsQuery.posture === "loading");
  const activePrograms = countActivePrograms(companyId, programsQuery.data?.items ?? []);
  const activeCohorts = countActiveCohorts(companyId, cohortsQuery.data?.items ?? []);
  // The list reads return at most one page (100). When a page is full or the API reports a next
  // cursor, more rows may exist, so the count is a floor, not a total.
  const programsPartial = mayHaveMore(programsQuery.data);
  const cohortsPartial = mayHaveMore(cohortsQuery.data);

  return (
    <Modal open={open} onClose={onClose} title="Archive company">
      <div className="flex flex-col gap-4">
        {topError && <Alert variant={topError.tone === "warning" ? "warning" : "error"}>{topError.message}</Alert>}

        <Alert variant="warning">
          Archiving <span className="font-semibold">{name}</span> removes it from active navigation.
        </Alert>

        {countsLoading ? (
          <p role="status" className="text-sm text-muted">
            Checking linked programs and cohorts…
          </p>
        ) : countsFailed ? (
          <p role="status" className="text-sm text-muted">
            The linked program and cohort counts couldn&apos;t be loaded. You can still archive this company.
          </p>
        ) : (
          <p className="text-sm text-strong">
            This company has {countPhrase(activePrograms, "active program", programsPartial)} and{" "}
            {countPhrase(activeCohorts, "active cohort", cohortsPartial)}.
          </p>
        )}

        <p className="text-sm text-muted">
          Linked programs and cohorts stay intact: archiving changes only the company. An archived company leaves the
          Companies list and can&apos;t be reactivated from the dashboard yet.
        </p>

        <div className="mt-2 flex items-center justify-end gap-3">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="danger"
            className={destructiveButtonClass}
            isLoading={submitting}
            onClick={() => {
              void onConfirm().catch(() => {
                // The owning mutation surfaces the error via `submitError`;
                // swallow so the dialog stays open with the inline error.
              });
            }}
          >
            Archive company
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function plural(count: number, noun: string) {
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}

const LIST_PAGE_CAP = 100;

function mayHaveMore(data: { items: unknown[]; next_cursor?: string | null } | undefined) {
  return Boolean(data?.next_cursor) || (data?.items.length ?? 0) >= LIST_PAGE_CAP;
}

function countPhrase(count: number, noun: string, partial: boolean) {
  return `${partial ? "at least " : ""}${plural(count, noun)}`;
}

function countActivePrograms(companyId: string, programs: CoachingProgramDto[]) {
  return programs.filter(
    (program) => (program.company_id == null || program.company_id === companyId) && program.status === "active"
  ).length;
}

function countActiveCohorts(companyId: string, cohorts: CoachingCohortDto[]) {
  return cohorts.filter(
    (cohort) => (cohort.company_id == null || cohort.company_id === companyId) && cohort.status === "active"
  ).length;
}
